import deburr from 'lodash.deburr';
import speakingurl from 'speakingurl';

import { pinyin } from '@gitbook/pinyin-pro';

import * as hepburn from './hepburn';

type Options = {
    custom?: string[] | { [key: string]: string };
    lang?: string;
    maintainCase?: boolean;
    replacement?: string;
    separator?: string;
    separateNumbers?: boolean;
    separateApostrophes?: boolean;
    tone?: boolean;
};

/**
 * Returns an appropriate slug for the given text string.
 *
 * Based on limax: https://github.com/lovell/limax
 */
export function getSlug(text: string, options: Options = {}) {
    text = text.replace(
        // biome-ignore lint/suspicious/noMisleadingCharacterClass: https://github.com/lovell/limax/blob/main/lib/limax.js#L22
        /(\S)['\u2018\u2019\u201A\u201B\u2032\u2035\u0301](\S)/g,
        options.separateApostrophes === true ? '$1 $2' : '$1$2'
    );

    // Break out any numbers contained within a word
    if (options.separateNumbers === true) {
        text = text.replace(/([^\d\s])([0-9]+)([^\d\s])/g, '$1 $2 $3');
    }

    // Should we remove the separator before a digit where previous word does not end in a digit?
    let mergeDigitSuffixes = false;

    // Language-specific behaviour
    const lang = options.lang || '';
    if (lang.toLowerCase().startsWith('ja') || hepburn.containsKana(text)) {
        // Convert from Japanese Kana using Hepburn romanisation
        text = hepburn.fromKana(text);
        // Remove any remaining non-Kana, e.g. Kanji
        text = text.replace(/([^A-Za-z0-9\- ]+)/g, '');
    } else if (lang.toLowerCase().startsWith('zh') || /[\u4e00-\u9fa5]+/.test(text)) {
        // Should we use tone numbers? (default is true)
        const tone = typeof options.tone === 'boolean' ? options.tone : true;
        mergeDigitSuffixes = tone;
        const data = pinyin(text, { type: 'all', toneType: 'none', nonZh: 'consecutive' }).map(
            (item) => {
                if (item.isZh) {
                    return tone ? `${item.pinyin}${item.num || ''}` : item.pinyin;
                }

                return item.origin;
            }
        );
        text = data.join(' ');
        // Remove punctuation symbols
        const customNonPunctuation = customCharsAsArray(options.custom)
            .map((c) => `\\${c}`)
            .join('');
        const nonPunctuationMatcher = new RegExp(`([^0-9A-Za-z ${customNonPunctuation}]+)`, 'g');
        text = text.replace(nonPunctuationMatcher, '');
        // Remove space around single character words, caused by non-Mandarin symbols in otherwise Mandarin text
        text = text.replace(/([^1-4]) ([A-Za-z]) /g, '$1$2');
    }
    // Convert to slug using speakingurl
    const separator = options.replacement || options.separator;
    const deburredText = deburr(text);
    const slug = speakingurl(deburredText, {
        lang: lang || 'en',
        separator: typeof separator === 'string' ? separator : '-',
        maintainCase: options.maintainCase || false,
        custom: options.custom || {},
    });
    // Remove separator before a digit where previous word does not end in a digit
    return mergeDigitSuffixes ? slug.replace(/([^0-9])-([0-9])/g, '$1$2') : slug;
}

function customCharsAsArray(custom?: string[] | { [key: string]: string }) {
    custom = custom || [];
    return Array.isArray(custom) ? custom : Object.keys(custom);
}
